// Pure client-side filtering for the Match Results widget (consumes [league,
// season] from the server; date/team narrowing happens here, no refetch).
// Unit-tested; the widget just renders the result.

import type { MatchSummary } from "@catbento/shared";

export interface MatchFilter {
  /** Restrict to matches involving this team id, or null for all teams. */
  teamId: number | null;
  /** Only completed matches (both scores present). */
  onlyPlayed: boolean;
}

/** Filter by team + played, then sort most-recent first (date, then kick-off). */
export function filterMatches(matches: MatchSummary[], f: MatchFilter): MatchSummary[] {
  let out = matches;
  if (f.teamId !== null) {
    out = out.filter((m) => m.homeTeamId === f.teamId || m.awayTeamId === f.teamId);
  }
  if (f.onlyPlayed) {
    out = out.filter((m) => m.homeScore !== null && m.awayScore !== null);
  }
  return [...out].sort((a, b) => {
    if (a.matchDate !== b.matchDate) return a.matchDate < b.matchDate ? 1 : -1;
    return (b.kickOff ?? "").localeCompare(a.kickOff ?? "");
  });
}

/** The distinct teams appearing in a match list, sorted by name — for the team
 *  filter dropdown. */
export function teamsFromMatches(matches: MatchSummary[]): { id: number; name: string }[] {
  const byId = new Map<number, string>();
  for (const m of matches) {
    byId.set(m.homeTeamId, m.homeTeamName);
    byId.set(m.awayTeamId, m.awayTeamName);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
